/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';

import {transformJsonProperty} from '../../components/util/adn-http-resource-transforms';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface ReportScheduleModel extends SignalAdminResourceModel {
  [key: string]: any;
}

const transform = transformJsonProperty('template');

@Injectable({providedIn: 'root'})
export class ReportScheduleApiService extends SignalAdminResourceApiBase<ReportScheduleModel> {
  constructor(client: AdnApiClientService) {
    super(client, 'reportschedules');
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<ReportScheduleModel>> {
    return super.list(params).pipe(map((page) => this.transformPage(page)));
  }

  override get(id: string): Observable<ReportScheduleModel> {
    return super.get(id).pipe(map((model) => transform.transformResponse(model)));
  }

  override save(model: ReportScheduleModel): Observable<ReportScheduleModel> {
    return super.save(transform.transformRequest(model)).pipe(
      map((saved) => transform.transformResponse(saved))
    );
  }

  saveRecipients(id: string, recipientUsers: any[]): Observable<ReportScheduleModel> {
    return super.save({id, recipientUsers} as ReportScheduleModel).pipe(
      map((saved) => transform.transformResponse(saved))
    );
  }

  listRelated(relatedId: string, relatedObjectType: string): Observable<SignalAdminResourcePage<ReportScheduleModel>> {
    return this.client.request<SignalAdminResourcePage<ReportScheduleModel>>({
      method: 'GET',
      url: this.resourceUri('reportschedules'),
      params: {
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        orderBy: 'name',
        pageSize: 1001,
        relatedId,
        relatedObjectType
      }
    }).pipe(map((page) => this.transformPage(page)));
  }

  private transformPage(page: SignalAdminResourcePage<ReportScheduleModel>): SignalAdminResourcePage<ReportScheduleModel> {
    return {
      ...page,
      results: (page.results || []).map((model) => transform.transformResponse(model))
    };
  }
}
